import { Div, Input, Select } from '@codesuma/baseline'
import { System, IDateParts, MONTHS, YEAR_RANGE, monthLength } from '../../services/convert'
import { toFaDigits } from '../../helpers/digits'
import style from './index.module.css'

// Year, month and day for one calendar system, emitting 'pick' with the chosen
// date. Month and day are selects so only real dates can be chosen — the day
// list is rebuilt whenever the month it belongs to changes length, which is
// where an adjusted hijri month shows up.
//
// The event is 'pick' and not 'change' on purpose. Baseline proxies any event
// name that is also a DOM event onto the root element, so a listener for
// 'change' on this Div would ALSO catch the native change bubbling up from the
// selects inside it — and be handed a raw Event instead of the date.
export const DatePicker = (system: System, initial: IDateParts) => {
    const base = Div()
    base.addClass(style.picker)

    const value: IDateParts = { ...initial }
    const range = YEAR_RANGE[system]

    // Fields are appended day-first: the page is RTL, so that puts them in the
    // order a Persian date is read, ۲۲ اسفند ۱۴۰۲.
    const field = (caption: string) => {
        const wrap = Div()
        wrap.addClass(style.field)
        const label = Div(caption)
        label.addClass(style.caption)
        wrap.append(label)
        base.append(wrap)
        return wrap
    }

    const day = Select()
    day.addClass(style.control)
    field('روز').append(day)

    const month = Select(MONTHS[system].map((text, i) => ({ value: String(i + 1), text })))
    month.addClass(style.control)
    field('ماه').append(month)

    const year = Input('', 'number', { value: value.year, min: range.min, max: range.max })
    year.addClass(style.control)
    field('سال').append(year)

    const fillDays = () => {
        const days = monthLength(system, value.year, value.month)
        if (value.day > days) value.day = days
        day.empty()
        for (let d = 1; d <= days; d++) day.add(String(d), toFaDigits(d))
        day.setValue(String(value.day))
    }

    const emit = () => base.emit('pick', { ...value })

    day.on('change', (v: string) => {
        value.day = +v
        emit()
    })

    month.on('change', (v: string) => {
        value.month = +v
        fillDays()
        emit()
    })

    // Ignore a half-typed or out-of-range year rather than snapping the field
    // out from under the user; the last good year stays applied.
    year.on('input', ({ value: typed }: { value: string }) => {
        const n = Number(typed)
        if (!Number.isInteger(n) || n < range.min || n > range.max) return
        value.year = n
        fillDays()
        emit()
    })

    month.setValue(String(value.month))
    fillDays()

    return base
}
